import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UI } from "../components/ActionDrawer";
import DashTable, {
  Notice,
  Oid,
  PageHead,
  Sub2,
  Tag,
} from "../components/DashTable";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";

const inr = (n) =>
  n === null || n === undefined || n === ""
    ? "—"
    : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

/* SL6. Orders sitting in one stage beyond the threshold. */
const StalledOrders = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const fetchRows = async () => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const res = await apiService.fetchStalledOrders(token);
      setRows(res.data || []);
      setSummary(res.summary || null);
    } catch (error) {
      setLoadFailed(true);
      toast.error("Error while fetching stalled orders");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    { label: "Order" },
    { label: "Clinic" },
    { label: "Stage" },
    { label: "Days in stage", num: true },
    { label: "Waiting on" },
    { label: "Value", num: true },
    { label: "Owner" },
  ];

  const tableRows = rows.map((row) => ({
    id: row.orderId,
    cells: [
      <>
        <Oid>{row.orderNumber}</Oid>
        <Sub2>{row.doctorName}</Sub2>
      </>,
      row.clinicName,
      <Tag tone="stage">{row.stage}</Tag>,
      <Box component="span" sx={{ color: UI.red, fontWeight: 500 }}>
        {row.daysInStage}d
      </Box>,
      row.waitingOn || "—",
      inr(row.orderTotal),
      row.ownerName || "—",
    ],
  }));

  const kpis = summary
    ? [
        { label: "Stalled", value: summary.stalled ?? rows.length, alarm: true },
        { label: "Value at risk", value: inr(summary.valueAtRisk) },
        { label: "Threshold", value: `${summary.thresholdDays ?? "—"} days` },
        { label: "Oldest", value: summary.oldestDays ? `${summary.oldestDays}d` : "—" },
      ]
    : [];

  return (
    <Box sx={{ padding: { xs: "18px 16px 50px", md: "24px 28px 60px" }, maxWidth: 1240 }}>
      <PageHead
        title="Stalled orders"
        lede="Orders sitting in one stage beyond the threshold."
        kpis={kpis}
      />

      <Notice>
        <b>Open decision.</b> The stall threshold and escalation path are
        undefined. Nothing here escalates automatically.
      </Notice>

      <DashTable
        columns={columns}
        rows={tableRows}
        onRowClick={(id) => navigate(`/dashboard/orders/${id}`)}
        emptyText="Nothing is stalled."
        errorText={loadFailed ? "Could not load stalled orders." : null}
        onRetry={fetchRows}
        foot={
          summary
            ? `${summary.stalled ?? rows.length} stalled · ${inr(
                summary.valueAtRisk,
              )} at risk`
            : null
        }
      />

      {isLoading && <PageLoader />}
    </Box>
  );
};

export default StalledOrders;
